import { IDataFlowArguments } from './dataflow-expression-utilities';
import { DataFlowExpression } from './dataflowexpression';
import { NullDataFlowType, AnyDataFlowType } from './dataflow-datatype';
import { ModelMarkerProvider } from 'app/authoring/component/transformations/transformation-expression-builder/model.marker.provider';

import { ComplexDataFlowType } from './dataflow-complex-datatype';
import { DataFlowUtils } from 'app/authoring/component/transformations/dataFlow-utils';
import { Transformation } from './transformation';

export async function asyncUpdateIdentifiers(availableArguments: IDataFlowArguments, expression: DataFlowExpression, transformationContext?: Transformation,
    typeDefinition?: boolean, setReturnType?: boolean): Promise<void> {

    const modelMarkerProvider = new ModelMarkerProvider(availableArguments);
    await modelMarkerProvider.asyncEvaluate(`${typeDefinition
        ? `${stringResources.Schema_Column_Default_Type_Placeholder} ? `
        : ''}${expression.expression}`, transformationContext?.owner, transformationContext);

    if (setReturnType) {
        expression.returnType = modelMarkerProvider.getExpressionReturnType();
    }
    handleComplexValue(availableArguments, expression, typeDefinition);
    handleExpression(modelMarkerProvider, expression);
}

export function updateIdentifiers(availableArguments: IDataFlowArguments, expression: DataFlowExpression, typeDefinition?: boolean): void {
    handleComplexValue(availableArguments, expression, typeDefinition);
    const modelMarkerProvider = new ModelMarkerProvider(availableArguments);
    modelMarkerProvider.evaluate(`${typeDefinition ? `${stringResources.Schema_Column_Default_Type_Placeholder} ? ` : ''}${expression.expression}`);
    handleExpression(modelMarkerProvider, expression);
}

function handleComplexValue(availableArguments: IDataFlowArguments, expression: DataFlowExpression, typeDefinition?: boolean): void {
    if (expression.expression && expression.returnType instanceof ComplexDataFlowType && expression.returnType.fields.length
            // This checks to exclude references to complex structures that should not be replaced with the actual structure (e.g. arrayOfStructures[0] or a.b where b is a structure)
            && (/^\@\([\s\S]*=[\s\S]*\)$/.test(expression.expression) || /^\([\s\S]* as [\s\S]*\)$/.test(expression.expression))) {
        const props: string[] = [];
        expression.expression = typeDefinition ? '(' : '@(';
        expression.returnType.fields.forEach((field) => {
            updateIdentifiers(availableArguments, field.dataFlowExpression, typeDefinition);
            const escapedName = DataFlowUtils.escapeFieldIfNeeded(field.entityName);
            props.push(`${escapedName}${typeDefinition ? ' as ' : '='}${field.dataFlowExpression.expression}`);
        });
        expression.expression += props.join(',\n\t\t');
        expression.expression += ')';
    }
}

function handleExpression(modelMarkerProvider: ModelMarkerProvider, expression: DataFlowExpression): void {
    expression.argumentsUsed = modelMarkerProvider.getUsedIdentifiers();
    expression.returnType = modelMarkerProvider.getExpressionReturnType();
    expression.clearExpressionErrors();
    let expressionHasError = !modelMarkerProvider.isEvaluatedProperly();
    let expressionMissingRequiredFunctions = false;

    if (expression.expression) {
        if (expression.requiredFunctions && expression.requiredFunctions.length > 0) {
            expressionMissingRequiredFunctions = !expression.argumentsUsed.dataFlowFunctions.some((usedFunction) =>
                expression.requiredFunctions.includes(usedFunction.dataFlowFunction));

            if (expressionMissingRequiredFunctions) {
                expression.addError();
            }
        }
        if (expression.returnType instanceof NullDataFlowType || (!expression.ignoreAnyTypeValidation && expression.returnType instanceof AnyDataFlowType)) {
            expressionHasError = true;
            expression.addError(expression.returnType instanceof NullDataFlowType
                    ? stringResources.DataFlowEditorErrorNullValue
                    : stringResources.DataFlowEditorErrorAnyValue);
        }
    }

    const unknownFuncWarning = modelMarkerProvider.getWarnings().find((warning) => warning.message === stringResources.DataFlowEditorErrorUnknownFunction);
    if (unknownFuncWarning) {
        expression.clearExpressionErrors();
        expression.addWarning(unknownFuncWarning.message);
        return;
    }

    modelMarkerProvider.getErrors().forEach((error) => expression.addError(error.message));
    modelMarkerProvider.getWarnings().forEach((warning) => expression.addWarning(warning.message));
    expression.updateExpressionErrorStatus(expressionHasError, modelMarkerProvider.hasError(), expressionMissingRequiredFunctions);
}
